import { Injectable, OnDestroy } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { filter, map, pairwise, withLatestFrom } from 'rxjs/operators';
import { ErrorType } from '@lib/interfaces/errors';
import { CommonLibService, PageLoadingService } from '@lib/services';
import { GetOrderRequest, DefaultService as api } from 'src/sdk-servicing';

import {
  GetOrderResultState,
  GetOrderResultStore,
  resetGetOrderResult,
  selectGetOrderResultState,
  setGetOrderResultFromApi,
} from '@common/store/get-order-result';

@Injectable()
export class GetOrderResultStoreService implements OnDestroy {
  private _subscriptions: Subscription[] = [];

  /** PNR情報取得APIのレスポンス */
  private _result$ = this._store.pipe(
    select(selectGetOrderResultState),
    pairwise(),
    filter(([prev, curr]) => prev.isPending === true && curr.isPending === false),
    map(([_, cur]) => cur)
  );

  /**
   * エラーが発生していないPNR情報取得APIのレスポンス通知
   */
  get success$(): Observable<GetOrderResultState> {
    return this._result$.pipe(filter((state) => state.isFailure === false));
  }

  /**
   * エラーが発生しているPNR情報取得APIのレスポンス通知
   */
  get failure$(): Observable<GetOrderResultState> {
    return this._result$.pipe(filter((state) => state.isFailure === true));
  }

  constructor(
    private _commonLibService: CommonLibService,
    private _pageLoadingService: PageLoadingService,
    private _store: Store<GetOrderResultStore>,
    private api: api
  ) {
    this.watchFailure();
  }

  ngOnDestroy(): void {
    this.unsubscribeAll();
    this._store.dispatch(resetGetOrderResult());
  }

  private unsubscribeAll() {
    this._subscriptions
      .filter((subscription) => subscription != null)
      .forEach((subscription) => subscription.unsubscribe());
  }

  /**
   * PNR情報取得APIを呼び出す
   * @param parameter PNR情報取得APIのリクエストパラメータ
   */
  request(parameter: GetOrderRequest): void {
    const call = this.api.ordersGetOrderPost(parameter);
    this._store.dispatch(setGetOrderResultFromApi({ call }));
  }

  /**
   * PNR情報取得結果ストアをクリアする
   */
  clear(): void {
    this._store.dispatch(resetGetOrderResult());
  }

  /**
   * PNR情報取得APIレスポンス異常取得
   */
  private watchFailure(): void {
    this._subscriptions.push(
      this.failure$
        .pipe(
          withLatestFrom(this._commonLibService.apiErrorResponseService.getApiErrorResponse$(), (_, state) => state)
        )
        .subscribe((state) => {
          const apiError = state?.errors?.[0];
          if (apiError) {
            this._commonLibService.errorsHandlerService.setNotRetryableError({
              errorType: ErrorType.SYSTEM,
              apiErrorCode: apiError.code,
            });
          }

          this._pageLoadingService.endLoading();
        })
    );
  }
}
